import { stripeElementsAddressElementModes } from "@circle-react/components/Paywalls/StripeElements/types";
import { AddressElement as StripeAddressElement } from "@stripe/react-stripe-js";
import type {
  StripeAddressElementChangeEvent,
  StripeAddressElementOptions,
} from "@stripe/stripe-js";
import { useCallback, useMemo } from "react";

import { useAddressElementDisplay } from "../../StripeElements/useAddressElementDisplay";
import { useAddressElementFields } from "../../StripeElements/useAddressElementFields";
import { usePaywallCheckoutAttemptsContext } from "./PaywallCheckoutAttemptsContext";

export const PaywallsCheckoutAttemptsAddressElement = () => {
  const {
    isAddressElementRequired,
    isMemberAddressComplete,
    memberAddress,
    memberName,
    setMemberAddress,
  } = usePaywallCheckoutAttemptsContext();

  const defaultValues = useMemo(() => {
    if (!memberAddress || !memberAddress.country) {
      return {};
    }
    return {
      address: {
        city: memberAddress.city,
        country: memberAddress.country,
        line1: memberAddress.line1,
        line2: memberAddress.line2,
        postalCode: memberAddress.postalCode,
        state: memberAddress.state,
      },
      name: memberAddress.name || memberName,
    };
  }, [memberAddress, memberName]);
  const display = useAddressElementDisplay();
  const fields = useAddressElementFields();
  const mode = stripeElementsAddressElementModes.billing;
  const options = useMemo<StripeAddressElementOptions>(
    () => ({
      defaultValues,
      display,
      fields,
      mode,
    }),
    [defaultValues, display, fields, mode],
  );

  const onChange = useCallback(
    (event: StripeAddressElementChangeEvent) => {
      const address = event.value.address || {};
      setMemberAddress({
        city: address.city || undefined,
        country: address.country || undefined,
        isComplete: event.complete,
        line1: address.line1 || undefined,
        line2: address.line2 || undefined,
        name: event.value.name || undefined,
        postalCode: address.postal_code || undefined,
        state: address.state || undefined,
      });
    },
    [setMemberAddress],
  );

  // Don't render if address element is not required OR member already has a complete
  // address on file. We check isMemberAddressComplete (the initial prop from backend)
  // instead of memberAddress.isComplete because the latter becomes true when the user
  // fills out the form, and we want to keep showing the form for review until submission.
  if (!isAddressElementRequired || isMemberAddressComplete) {
    return null;
  }

  return <StripeAddressElement options={options} onChange={onChange} />;
};
